import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircleIcon, XIcon } from 'lucide-react';
import { formatPrice } from '../utils/format';
import { productOrderUrl, WHATSAPP_DISPLAY } from '../utils/whatsapp';
import type { Product } from '../types/catalog';
interface ProductDialogProps {
  product: Product | null;
  onClose: () => void;
}
export function ProductDialog({
  product,
  onClose
}: ProductDialogProps) {
  useEffect(() => {
    if (!product) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [product, onClose]);
  return <AnimatePresence data-id="element-59" data-mp="32pdw5biiv5mw">
      {product && <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={`Ficha de ${product.name}`} data-id="element-60" data-mp="37n5og6n2wtrm">
          <motion.button type="button" aria-label="Cerrar ficha" onClick={onClose} className="absolute inset-0 h-full w-full cursor-default bg-navy/60" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-61" data-mp="k8ddywb6tvgw" />

          <motion.div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-y-auto rounded-t-3xl bg-ivory sm:max-h-[88vh] sm:rounded-3xl" initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-62" data-mp="134vdw106x1o8">
            <button type="button" onClick={onClose} aria-label="Cerrar ficha" className="absolute right-3 top-3 z-10 rounded-full bg-ivory/90 p-2 text-navy transition-colors duration-200 ease-smooth hover:bg-navy hover:text-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-63" data-mp="1lxzbtykfy0on">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-64" data-mp="27oimeuvwxkwd" />
            </button>

            <div className="grid sm:grid-cols-2" data-id="element-65" data-mp="2cnv4717xg1s3">
              <img src={product.image} alt={`Frasco de ${product.name}`} className="aspect-[4/5] w-full bg-white object-contain" data-id="element-66" data-mp="2cn27kt2omsno" />

              <div className="flex flex-col p-6 sm:p-8" data-id="element-67" data-mp="2lotaxr9r2ned">
                <p className="text-[11px] font-bold uppercase tracking-wide text-cobalt" data-id="element-68" data-mp="2qnmmhe5h84bw">
                  {product.family}
                </p>
                <h2 className="mt-2 font-display text-3xl font-extrabold leading-tight text-navy" data-id="element-69" data-mp="rbtn5av7umw0">
                  {product.name}
                </h2>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-mute" data-id="element-70" data-mp="oq2nha8jx7g0">
                  {product.size} · {product.concentration}
                </p>

                <p className="mt-4 text-sm leading-relaxed text-mute" data-id="element-71" data-mp="ezrayzasxpxi">{product.description}</p>

                <dl className="mt-5 space-y-2 border-t border-line pt-5 text-sm" data-id="element-72" data-mp="a0ogg8sg723s">
                  {[{
                label: 'Salida',
                values: product.notes.salida
              }, {
                label: 'Corazón',
                values: product.notes.corazon
              }, {
                label: 'Fondo',
                values: product.notes.fondo
              }].map(group => <div key={group.label} className="flex gap-4" data-id="element-73" data-mp="xoxv8qr8uplf">
                      <dt className="w-20 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-cobalt" data-id="element-74" data-mp="srnzyuh6bjpo">
                        {group.label}
                      </dt>
                      <dd className="text-navy" data-id="element-75" data-mp="3dhde15a1z1hb">{group.values.join(', ')}</dd>
                    </div>)}
                </dl>

                <p className="mt-5 border-t border-line pt-5 text-xs leading-relaxed text-mute" data-id="element-76" data-mp="165esgikowqcp">
                  <span className="font-semibold uppercase tracking-wide" data-id="element-77" data-mp="1axoq6ks1qupj">Ingredientes · </span>
                  {product.ingredients.join(' · ')}
                </p>

                <div className="mt-6" data-id="element-78" data-mp="116kb7l2df9up">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-cobalt" data-id="element-79" data-mp="117lmnqnatycy">
                    Precio de lanzamiento
                  </p>
                  <p className="mt-1 flex items-baseline gap-3" data-id="element-80" data-mp="1tvhk4xkozuas">
                    <span className="font-display text-3xl font-extrabold text-navy" data-id="element-81" data-mp="1ttoixxsum2a6">
                      {formatPrice(product.price)}
                    </span>
                    {product.compareAtPrice && <span className="text-base text-mute line-through" data-id="element-82" data-mp="3jbjh8bs14k9">
                        {formatPrice(product.compareAtPrice)}
                      </span>}
                  </p>
                </div>

                <div className="mt-4 flex flex-col gap-3" data-id="element-83" data-mp="2mpea95znsi87">
                  <a href={productOrderUrl(product.name)} target="_blank" rel="noreferrer" className="flex w-full items-center justify-center gap-2 rounded-full bg-lime px-6 py-4 text-xs font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-84" data-mp="2ms0fv02zonns">
                    <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-85" data-mp="1z1wburlw2lw9" />
                    Pedir por WhatsApp
                  </a>
                  <p className="text-center text-[11px] text-mute" data-id="element-86" data-mp="2ctuhutkb01o7">
                    Te atendemos al {WHATSAPP_DISPLAY}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}